<template>
  <div class="contrainer">
    <div class="kitchen-header">
      <div class="left-section">
        <van-field
          v-model="selectedLocation"
          readonly
          clickable
          :border="false"
          placeholder="选择餐厅"
          class="location-select"
          @click="showPicker = true"
        >
          <template #right-icon>
            <van-icon name="arrow-down" class="arrow-icon" />
          </template>
        </van-field>

        <div class="alert-tag">
          <van-icon name="warning" />
          <span>7道菜品，14份</span>
        </div>
      </div>

      <div class="right-section">
        <van-button
          :class="['view-btn', { active: currentView === 'order' }]"
          @click="currentView = 'order'"
        >
          订单视图
        </van-button>
        <van-button
          :class="['view-btn', { active: currentView === 'dish' }]"
          @click="currentView = 'dish'"
        >
          菜品视图
        </van-button>
      </div>
    </div>
    <div class="kitchen-body">
      <KitchenOrderCompent v-if="currentView === 'order'" :order-list="mockOrders" />
      <KitchenCommodityNewCompent
        v-else-if="currentView === 'dish'"
        :table-data="tableData"
        :stoves-data="stovesData"
      />
      <div style="height: 20vh"></div>
    </div>
  </div>

  <!-- 在van-field下方添加picker -->
  <van-popup v-model:show="showPicker" round position="bottom" :style="{ height: '50%' }">
    <van-picker :columns="locationOptions" @confirm="onConfirm" @cancel="showPicker = false" />
  </van-popup>
</template>

<script setup lang="ts">
  import { onMounted, ref, onUnmounted } from 'vue';
  import KitchenOrderCompent from '@/components/KitchenOrderCompent.vue';
  import KitchenCommodityNewCompent from '@/components/KitchenCommodityNewCompent.vue';
  import {
    OrderDataModel,
    KitchenCommodityCardDataModel,
    DishItemNewModel,
    StoveItemModel,
  } from '@/model/kitchen';
  import { fetchShopList } from '@/service/base';

  const selectedLocation = ref('24小时餐厅');
  const locationOptions = [
    { text: '24小时餐厅', value: '24小时餐厅' },
    { text: '宴会厅', value: '宴会厅' },
    { text: '五楼中央厨房', value: '五楼中央厨房' },
    { text: '三楼中庭吧台', value: '三楼中庭吧台' },
  ];

  const currentView = ref<'order' | 'dish'>('order');

  const showPicker = ref(false);

  const onConfirm = ({ selectedValues }: { selectedValues: string[] }) => {
    selectedLocation.value = selectedValues[0];
    showPicker.value = false;
  };

  // 创建模拟订单数据
  const mockOrders = [
    {
      id: 1,
      restaurantName: '24小时餐厅',
      tableNumber: 'A1',
      createDate: '2023-09-15 18:30:00',
      orderId: 'ORD123456',
      dishes: [
        { id: 'D1', name: '红烧牛肉面', quantity: 2, note: '少放辣' },
        { id: 'D2', name: '宫保鸡丁饭', quantity: 1, note: '不要花生' },
        { id: 'D3', name: '清蒸鲈鱼', quantity: 1 },
        { id: 'D9', name: '蒜蓉粉丝蒸扇贝', quantity: 4 },
        { id: 'D10', name: '上汤娃娃菜', quantity: 1 },
      ],
      status: '进行中',
      estimatedTime: '15分钟',
      customerName: '张先生',
    },
    {
      id: 2,
      restaurantName: '24小时餐厅',
      tableNumber: 'B2',
      createDate: '2023-09-15 19:15:00',
      orderId: 'ORD123457',
      dishes: [
        { id: 'D4', name: '扬州炒饭', quantity: 3 },
        { id: 'D5', name: '酸辣汤', quantity: 2 },
        { id: 'D11', name: '糖醋排骨', quantity: 1 },
        { id: 'D12', name: '干锅肥肠', quantity: 1 },
      ],
      status: '已完成',
      completedTime: '2023-09-15 19:45:00',
      customerName: '李女士',
    },
    {
      id: 3,
      restaurantName: '宴会厅',
      tableNumber: 'VIP888',
      createDate: '2023-09-15 20:00:00',
      orderId: 'ORD123458',
      dishes: [
        { id: 'D6', name: '北京烤鸭', quantity: 2, note: '片皮要薄' },
        { id: 'D7', name: '鲍鱼捞饭', quantity: 4 },
        { id: 'D8', name: '清炒时蔬', quantity: 2 },
        { id: 'D13', name: '佛跳墙', quantity: 1 },
        { id: 'D14', name: '龙虾刺身', quantity: 1 },
        { id: 'D15', name: '燕窝羹', quantity: 2 },
      ],
      status: '已取消',
      cancelReason: '客户临时有事',
      customerName: '王总',
    },
    {
      id: 4,
      restaurantName: '五楼中央厨房',
      tableNumber: 'C3',
      createDate: '2023-09-15 20:30:00',
      orderId: 'ORD123459',
      dishes: [
        { id: 'D16', name: '黑椒牛排', quantity: 2, note: '七分熟' },
        { id: 'D17', name: '奶油蘑菇汤', quantity: 2 },
        { id: 'D18', name: '凯撒沙拉', quantity: 1 },
      ],
      status: '进行中',
      estimatedTime: '20分钟',
      customerName: '刘先生',
    },
    {
      id: 5,
      restaurantName: '三楼中庭吧台',
      tableNumber: 'D4',
      createDate: '2023-09-15 21:00:00',
      orderId: 'ORD123460',
      dishes: [
        { id: 'D19', name: '莫吉托', quantity: 3 },
        { id: 'D20', name: '长岛冰茶', quantity: 2 },
        { id: 'D21', name: '水果拼盘', quantity: 1 },
      ],
      status: '已完成',
      completedTime: '2023-09-15 21:20:00',
      customerName: '林小姐',
    },
    {
      id: 5,
      restaurantName: '三楼中庭吧台',
      tableNumber: 'D4',
      createDate: '2023-09-15 21:00:00',
      orderId: 'ORD123460',
      dishes: [
        { id: 'D19', name: '莫吉托', quantity: 3 },
        { id: 'D20', name: '长岛冰茶', quantity: 2 },
        { id: 'D21', name: '水果拼盘', quantity: 1 },
      ],
      status: '已完成',
      completedTime: '2023-09-15 21:20:00',
      customerName: '林小姐',
    },
    {
      id: 5,
      restaurantName: '三楼中庭吧台',
      tableNumber: 'D4',
      createDate: '2023-09-15 21:00:00',
      orderId: 'ORD123460',
      dishes: [
        { id: 'D19', name: '莫吉托', quantity: 3 },
        { id: 'D20', name: '长岛冰茶', quantity: 2 },
        { id: 'D21', name: '水果拼盘', quantity: 1 },
      ],
      status: '已完成',
      completedTime: '2023-09-15 21:20:00',
      customerName: '林小姐',
    },
  ];
  const mockDishs: Array<KitchenCommodityCardDataModel> = [
    {
      dishName: '红烧牛肉面',
      isUrgent: true,
      restaurant: '24小时餐厅',
      table: 'A1',
      remaining: 2,
      time: '2023-09-15 18:30:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
    {
      dishName: '宫保鸡丁饭',
      isUrgent: false,
      restaurant: '24小时餐厅',
      table: 'B2',
      remaining: 0,
      time: '2023-09-15 19:15:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
    {
      dishName: '清蒸鲈鱼',
      isUrgent: true,
      restaurant: '宴会厅',
      table: 'VIP888',
      remaining: 1,
      time: '2023-09-15 20:00:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
    {
      dishName: '北京烤鸭',
      isUrgent: false,
      restaurant: '宴会厅',
      table: 'VIP666',
      remaining: 3,
      time: '2023-09-15 20:30:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
    {
      dishName: '黑椒牛排',
      isUrgent: true,
      restaurant: '五楼中央厨房',
      table: 'C3',
      remaining: 2,
      time: '2023-09-15 21:00:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
    {
      dishName: '莫吉托',
      isUrgent: false,
      restaurant: '三楼中庭吧台',
      table: 'D4',
      remaining: 0,
      time: '2023-09-15 21:30:00',
      stoves: ['1号灶台', '2号灶台', '3号灶台', '4号灶台', '5号灶台', '6号灶台'],
    },
  ];
  const stovesData: Array<StoveItemModel> = [
    { id: 1, name: '1号灶台', assignedTasks: 3, availableTasks: 5 },
    { id: 2, name: '2号灶台', assignedTasks: 2, availableTasks: 6 },
    { id: 3, name: '3号灶台', assignedTasks: 4, availableTasks: 4 },
    { id: 4, name: '4号灶台', assignedTasks: 1, availableTasks: 7 },
    { id: 5, name: '5号灶台', assignedTasks: 5, availableTasks: 3 },
    { id: 6, name: '6号灶台', assignedTasks: 0, availableTasks: 8 },
  ];
  const tableData: Array<DishItemNewModel> = [
    {
      name: '宫保鸡丁',
      type: '热菜',
      isUrgent: true,
      location: '宴会厅【8号桌】',
      portions: 2,
      time: '12:30',
    },
    {
      name: '水果拼盘',
      type: '冷菜',
      isUrgent: false,
      location: '包间【3号】',
      portions: 1,
      time: '12:45',
    },
    {
      name: '小笼包',
      type: '点心',
      isUrgent: false,
      location: '大厅【5号桌】',
      portions: 3,
      time: '12:20',
    },
    {
      name: '可乐',
      type: '饮品',
      isUrgent: true,
      location: '宴会厅【10号桌】',
      portions: 4,
      time: '12:35',
    },
    {
      name: '水煮鱼',
      type: '热菜',
      isUrgent: false,
      location: '包间【2号】',
      portions: 2,
      time: '12:50',
    },
    {
      name: '凉拌黄瓜',
      type: '冷菜',
      isUrgent: false,
      location: '大厅【7号桌】',
      portions: 1,
      time: '12:25',
    },
    {
      name: '冰淇淋',
      type: '点心',
      isUrgent: true,
      location: '包间【1号】',
      portions: 1,
      time: '12:40',
    },
  ];

  onMounted(async () => {
    const respShop = await fetchShopList({ pageNum: 1, pageSize: 100, data: {} });
    if (respShop.code === 200) {
      console.log(respShop.result.list);
    }
  });
</script>

<style scoped lang="less">
  .kitchen-body {
    height: 80%;
    overflow-y: auto;
    padding: 18px;
  }
  .location-select {
    width: 40%;
    text-align: center;
    padding: 0 !important;
    background-color: #393b3b !important;
    :deep(.van-field__control) {
      font-size: 20px;
      font-weight: bold;
      color: white;
      background-color: #393b3b !important;
    }
    :deep(.van-field__body) {
      background: #393b3b !important;
      border: 2px solid blue;
      padding: 15px;
      border-radius: 12px;
    }
    .arrow-icon {
      color: white;
      font-size: 16px;
    }
  }
  .contrainer {
    background: url('@/assets/bg.jpg') no-repeat center/cover;
    height: 100vh;
  }
  .kitchen-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;

    .left-section {
      display: flex;
      align-items: center;
      gap: 12px;

      .alert-tag {
        padding: 8px 12px;
        background: linear-gradient(45deg, #ff6057, #ff7d7c);
        border-radius: 12px;
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: bold;
        color: white;

        .van-icon {
          font-size: 18px;
        }
      }
    }

    .right-section {
      display: flex;
      gap: 8px;

      .view-btn {
        padding: 8px 16px;
        border-radius: 4px;
        background-color: #393b3b;
        color: #88868d;
        border: none;

        &.active {
          background-color: #1989fa;
          color: white;
        }
      }
    }
  }
</style>
